<template>
  <d-container
    fluid
    class="main-content-container js-disable-sticky-header px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="4"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('dashboard.mainPage') }}</span>
        <h3 class="page-title">{{ $t('dashboard.welcomeToHeadwear') }}</h3>
      </d-col>
    </d-row>

    <d-alert
      v-if="notificationsData && notificationsData.notifications"
      show
      theme="warning"
      class="d-flex">
      <h4 class="text-white mb-0 mt-0">{{ $t('notifications.youHaveUpcomingEvents') }}: {{ notificationsData.notifications }}</h4>
      <d-button
        @click.prevent="$router.push('/powiadomienia/najblizsze')"
        theme="light"
        class="ml-auto">
        {{ $t('notifications.seeList') }}
      </d-button>
    </d-alert>

    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col class="text-center text-sm-left mb-4 mb-sm-0">
        <h3 class="page-title">
          <template v-if="userIsAdmin">{{ $t('dashboard.workersTasksTitle') }}</template>
          <template v-else>{{ $t('dashboard.myTasksTitle') }}</template>
        </h3>
      </d-col>
    </d-row>

    <d-row>
      <d-col class="mb-4 columns-4">
        <d-card
          :class="{
            'p-4': true,
            'text-center': true,
            'is-loading': !loadedTasks
          }">
          <h6>
            <template v-if="loadedTasks">
              <template v-if="userIsAdmin">{{ $t('dashboard.offersToCheck') }}</template>
              <template v-else>{{ $t('dashboard.checkOffers') }}</template>
            </template>
            <template v-else>--</template>
          </h6>
          <h3>
            <template v-if="loadedTasks">
              {{ tasksData.check_offers_count }}
            </template>
            <template v-else>--</template>
          </h3>
          <d-button
            :disabled="!loadedTasks || tasksData.check_offers_count === 0"
            @click.prevent="openPopup('OFFERS_TO_CHECK', 'OFFERS_TO_CHECK')">
            {{ $t('dashboard.showOffers') }}
          </d-button>
        </d-card>
      </d-col>

      <d-col class="mb-4 columns-4">
        <d-card
          :class="{
            'p-4': true,
            'text-center': true,
            'is-loading': !loadedTasks
          }">
          <h6>
            <template v-if="loadedTasks">
              <template v-if="userIsAdmin">{{ $t('dashboard.inquiriesToSendToCustomers') }}</template>
              <template v-else>{{ $t('dashboard.sendInquiriesToCustomers') }}</template>
            </template>
            <template v-else>--</template>
          </h6>
          <h3>
            <template v-if="loadedTasks && tasksData.customers_to_send_inquiries">
              {{ tasksData.customers_to_send_inquiries.length }}
            </template>
            <template v-else>--</template>
          </h3>
          <d-button
            :disabled="!loadedTasks || (tasksData.customers_to_send_inquiries && tasksData.customers_to_send_inquiries.length === 0)"
            @click.prevent="openCustomersPopup()">
            {{ $t('dashboard.showCustomers') }}
          </d-button>
        </d-card>
      </d-col>

      <d-col
        v-for="(kpi, index) of tasksData.kpi"
        :key="'kpi-item-' + index"
        class="mb-4 columns-4">
        <d-card
          :class="{
            'p-4': true,
            'text-center': true,
            'is-loading': !loadedTasks
          }">
          <h6>
            <template v-if="loadedTasks">
              <template v-if="userIsAdmin">{{ $t('dashboard.admin_' + index) }}</template>
              <template v-else>{{ $t('dashboard.admin_' + index) }}</template>
            </template>
            <template v-else>--</template>
          </h6>
          <h3>
            <template v-if="loadedTasks && kpi.target - kpi.realization > 0">{{ kpi.target - kpi.realization }}</template>
            <template v-else-if="loadedTasks && kpi.target - kpi.realization <= 0">
              <span :class="getTextColor(kpi.realization_percent)">
                {{ kpi.realization_percent }}%
              </span>
            </template>
            <template v-else>--</template>
          </h3>
          <small
            v-if="loadedTasks"
            class="pt-2 pb-2">
            <span :class="getTextColor(kpi.realization_percent)">
              {{ kpi.realization_percent }}%
            </span>
            ({{ kpi.realization }} {{ $t('dashboard.from') }} {{ kpi.target }})
          </small>
        </d-card>
      </d-col>
    </d-row>

    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        cols="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <h3 class="page-title">{{ $t('dashboard.orderStatsTitle') }}</h3>
      </d-col>

      <d-col
        v-if="userIsWorker"
        cols="6"
        class="text-right">
        <d-checkbox
          inline
          v-model="showAllOrders"
          toggle
          toggle-small>
          {{ $t('order.showOtherWorkersData') }}
        </d-checkbox>
      </d-col>
    </d-row>

    <!-- Small Stats Blocks -->
    <d-row>
      <d-col
        lg
        v-for="(statsItem, idx) in stats"
        :key="idx"
        :class="'mb-4 ' + statsItem.cssClasses">
        <d-card
          :class="{
            'p-4': true,
            'text-center': true,
            'is-loading': !loadedDataParts[statsItem.part]
          }">
          <h6>
            <template v-if="loadedDataParts[statsItem.part]">{{ statsItem.label }}</template>
            <template v-else>--</template>
          </h6>
          <h3
            :class="{ 'text-danger' : loadedDataParts[statsItem.part] && statsData[statsItem.key + '_exclamation'] }">
            <template v-if="loadedDataParts[statsItem.part]">{{ statsData[statsItem.key] }}</template>
            <template v-else>--</template>
            <i
              v-if="loadedDataParts[statsItem.part] && statsData[statsItem.key + '_exclamation']"
              class="material-icons alert-icon">
              report_problem
            </i>
          </h3>
          <d-button
            :disabled="!loadedDataParts[statsItem.part]"
            @click.prevent="openPopup(statsItem.filter, statsItem.status, statsItem.dashboardParam)">
            {{ $t('order.showOrders') }}
          </d-button>
        </d-card>
      </d-col>

      <d-col
        lg
        key="long-realized-orders-stats"
        class="mb-4 columns-3">
        <d-card
          :class="{
            'p-4': true,
            'text-center': true,
            'is-loading': !loadedLongRealizedOrders
          }">
          <h6>
            <template v-if="loadedLongRealizedOrders">{{ $t('dashboard.longRealizedOrders') }}</template>
            <template v-else>--</template>
          </h6>
          <h3 :class="{ 'text-danger': longRealizedOrders.length }">
            <template v-if="loadedLongRealizedOrders">{{ longRealizedOrders.length }}</template>
            <template v-else>--</template>
            <i
              v-if="longRealizedOrders.length"
              class="material-icons alert-icon">
              report_problem
            </i>
          </h3>
          <d-button
            :disabled="!loadedLongRealizedOrders"
            @click.prevent="openLongRealizedOrdersPopup()">
            {{ $t('order.showOrders') }}
          </d-button>
        </d-card>
      </d-col>
    </d-row>

    <services-popup
      v-if="showServicesPopup"
      :orderData="this.orderData" />

    <orders-popup
      v-if="showOrdersPopup"
      :popupFilter="popupFilter"
      :popupStatus="popupStatus"
      :dashboardParam="popupPassDashboardParam"
      :popupShowAll="showAllOrders" />

    <customers-popup
      v-if="showCustomersPopup"
      :customersData="tasksData.customers_to_send_inquiries" />

    <contractors-settlements-popup
      v-if="showContractorsSettlementsPopup"
      :dashboardParam="popupPassDashboardParam"
      :popupShowAll="showAllOrders"
      :canClosePopup="!showContractorsSettlementsImportPopup" />

    <contractors-settlements-import-popup
      v-if="showContractorsSettlementsImportPopup" />

    <long-realized-orders-popup
      v-if="showLongRealizedOrdersPopup"
      :orders="longRealizedOrders"
      :popupShowAll="showAllOrders" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import ContractorsSettlementsPopup from './../components/dashboard/ContractorsSettlementsPopup.vue';
import ContractorsSettlementsImportPopup from './../components/dashboard/ContractorsSettlementsImportPopup.vue';
import FormUtils from './../utils/FormUtils.js';
import CustomersPopup from './../components/dashboard/CustomersPopup.vue';
import LongRealizedOrdersPopup from './../components/dashboard/LongRealizedOrdersPopup.vue';
import OrdersPopup from './../components/dashboard/OrdersPopup.vue';
import ServicesPopup from './../components/dashboard/ServicesPopup.vue';

export default {
  name: 'dashboard',
  data () {
    return {
      currencies: [],
      loadError: false,
      loadErrorParts: {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false
      },
      loadedDataParts: {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false
      },
      notificationsData: false,
      loadedCurrencies: false,
      loadedLongRealizedOrders: false,
      loadedTasks: false,
      loadedNotifications: false,
      longRealizedOrders: [],
      statsData: {},
      tasksData: {},
      showAllOrders: false,
      showContractorsSettlementsImportPopup: false,
      showContractorsSettlementsPopup: false,
      showCustomersPopup: false,
      showLongRealizedOrdersPopup: false,
      showOrdersPopup: false,
      showServicesPopup: false,
      orderData: null,
      popupFilter: '',
      popupStatus: '',
      popupPassDashboardParam: false
    };
  },
  components: {
    'contractors-settlements-import-popup': ContractorsSettlementsImportPopup,
    'contractors-settlements-popup': ContractorsSettlementsPopup,
    'customers-popup': CustomersPopup,
    'long-realized-orders-popup': LongRealizedOrdersPopup,
    'orders-popup': OrdersPopup,
    'services-popup': ServicesPopup
  },
  computed: {
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    userIsWorker () {
      return this.$store.getters.getUserType === 'worker';
    },
    stats () {
      return [
        {
          label: this.$t('dashboard.awaitingSample'),
          key: 'obtaining_count',
          filter: 'OBTAINING',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: true,
          part: 1
        },
        {
          label: this.$t('dashboard.awaitingSampleAccept'),
          key: 'accepting_count',
          filter: 'ACCEPTING',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: true,
          part: 2
        },
        {
          label: this.$t('dashboard.awaitingProductionFinish'),
          key: 'ending_count',
          filter: 'ENDING_PRODUCTION',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: true,
          part: 3
        },
        {
          label: this.$t('dashboard.awaitingDraft'),
          key: 'draft_count',
          filter: 'DRAFT',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: true,
          part: 5
        },
        {
          label: this.$t('dashboard.approachingDeadline'),
          key: 'deadline_count',
          filter: 'DEADLINE',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 4
        },
        {
          label: this.$t('dashboard.finishingProduction'),
          key: 'shipment_count',
          filter: 'SHIPMENT',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: true,
          part: 5
        },
        {
          label: this.$t('dashboard.toSettleWithContractors'),
          key: 'to_settlement_count',
          filter: 'CONTRACTORS_SETTLEMENTS',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 2
        },
        {
          label: this.$t('dashboard.inPreprationForShipping'),
          key: 'preparation_for_shipping_count',
          filter: false,
          status: 'PREPARATION_FOR_SHIPPING',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 1
        },
        {
          label: this.$t('dashboard.ordersPendingShipment'),
          key: 'waiting_for_shipping_count',
          filter: false,
          status: 'WAITING_FOR_SHIPPING',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 1
        },
        {
          label: this.$t('dashboard.ordersPendingInvoice'),
          key: 'without_settlement_count',
          filter: 'WITHOUT_SETTLEMENT',
          status: false,
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 2
        },
        {
          label: this.$t('dashboard.blockedOrders'),
          key: 'blocked_count',
          filter: false,
          status: 'BLOCKADE',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 4
        },
        {
          label: this.$t('dashboard.ordersInProduction'),
          key: 'in_production_count',
          filter: false,
          status: 'IN_PRODUCTION',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 3
        },
        {
          label: this.$t('dashboard.inquiruesPendingCustomerResponse'),
          key: 'sent_to_customer_count',
          filter: false,
          status: 'SENT_TO_CUSTOMER',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 3
        },
        {
          label: this.$t('dashboard.ordersInWarehouse'),
          key: 'collected_in_warehouse_count',
          filter: 'COLLECTED_BY_WAREHOUSEMAN',
          status: 'ORDER',
          cssClasses: 'columns-3',
          dashboardParam: false,
          part: 4
        }
      ];
    }
  },
  mounted () {
    this.loadConfig();
    this.loadAdditionalData();
    this.loadStatsData();
    this.loadTasksData();

    this.$bus.$on('dashboard-service-popup-open', this.openServicesPopup);
    this.$bus.$on('dashboard-service-popup-close', this.closeServicesPopup);
    this.$bus.$on('dashboard-orders-popup-close', this.closeOrdersPopup);
    this.$bus.$on('dashboard-customers-popup-close', this.closeCustomersPopup);
    this.$bus.$on('dashboard-contractors-settlements-popup-close', this.closeContractorsSettlementsPopup);
    this.$bus.$on('dashboard-contractors-settlements-import-popup-close', this.closeContractorsSettlementsImportPopup);
    this.$bus.$on('dashboard-contractors-settlements-import-popup-open', this.openContractorsSettlementsImportPopup);
    this.$bus.$on('dashboard-long-realized-orders-popup-close', this.closeLongRealizedOrdersPopup);
  },
  methods: {
    loadConfig () {
      if (!this.userIsWorker) {
        this.$watch('showAllOrders', () => {
          this.debouncedLoadStatsData();
        });
        return;
      }

      if (window.localStorage && window.localStorage.getItem('headwear-crm-dashboard-show-all-orders')) {
        this.showAllOrders = true;
      } else {
        this.showAllOrders = false;
      }

      this.$watch('showAllOrders', newValue => {
        if (newValue) {
          window.localStorage.setItem('headwear-crm-dashboard-show-all-orders', true);
        } else {
          window.localStorage.removeItem('headwear-crm-dashboard-show-all-orders');
        }

        this.debouncedLoadStatsData();
      });
    },
    debouncedLoadStatsData: debounce(function () {
      this.loadedDataParts[1] = false;
      this.loadedDataParts[2] = false;
      this.loadedDataParts[3] = false;
      this.loadedDataParts[4] = false;
      this.loadedDataParts[5] = false;
      this.loadStatsData();
      this.loadLongRealizedOrders();
    }, 500),
    loadStatsData () {
      this.loadStatsParts(1);
      this.loadStatsParts(2);
      this.loadStatsParts(3);
      this.loadStatsParts(4);
      this.loadStatsParts(5);
    },
    loadTasksData () {
      this.$http.get('/dashboard/tasks').then(response => {
        if (!response.data) {
          this.loadedTasks = true;
          return;
        }

        this.tasksData = response.data;
        this.loadedTasks = true;
      }).catch(error => {
        console.log(error);
        this.loadedTasks = true;
      });
    },
    loadStatsParts (part) {
      let parts = {
        1: [
          'obtaining_count',
          'obtaining_count_exclamation',
          'preparation_for_shipping_count',
          'waiting_for_shipping_count'
        ],
        2: [
          'accepting_count',
          'accepting_count_exclamation',
          'to_settlement_count',
          'without_settlement_count'
        ],
        3: [
          'ending_count',
          'ending_count_exclamation',
          'sent_to_customer_count',
          'in_production_count'
        ],
        4: [
          'deadline_count',
          'deadline_count_exclamation',
          'blocked_count',
          'collected_in_warehouse_count'
        ],
        5: [
          'draft_count',
          'draft_count_exclamation',
          'shipment_count',
          'shipment_count_exclamation'
        ]
      };

      let showAllParam = '';

      if (this.showAllOrders) {
        showAllParam = '?show_all=true';
      }

      this.$http.get('/dashboard/part/' + part + showAllParam).then(response => {
        if (!response.data) {
          this.loadErrorParts[part] = true;
          return;
        }

        for (let i = 0; i < parts[part].length; i++) {
          let key = parts[part][i];
          this.statsData[key] = response.data[key];
        }

        this.loadedDataParts[part] = true;
      }).catch(error => {
        console.log(error);
        this.loadErrorParts[part] = true;
      });
    },
    loadLongRealizedOrders () {
      let config = {
        method: 'get',
        endpoint: '/dashboard/long-realized-orders',
        loadedKey: 'loadedLongRealizedOrders',
        outputKey: 'longRealizedOrders',
        errorKey: 'loadError'
      };

      if (this.showAllOrders) {
        config.params = {
          show_all: true
        };
      }

      this.loadedLongRealizedOrders = false;

      FormUtils.loadAdditionalData(this, config);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      this.loadLongRealizedOrders();

      FormUtils.loadAdditionalData(this, {
        endpoint: '/notifications/items',
        loadedKey: 'loadedNotifications',
        outputKey: 'notificationsData',
        errorKey: 'loadError',
        params: {
          where: {
            is_near: 1
          },
          return: 'count'
        }
      });
    },
    isWaitingTooLong (dateString) {
      let datePoint = new Date();
      datePoint.setDate(datePoint.getDate() - 3);
      return new Date(dateString).getTime() < datePoint.getTime();
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    openCustomersPopup () {
      this.showCustomersPopup = true;
    },
    openServicesPopup (orderData) {
      this.orderData = JSON.parse(JSON.stringify(orderData));
      this.showServicesPopup = true;
    },
    openContractorsSettlementsImportPopup () {
      this.showContractorsSettlementsImportPopup = true;
    },
    openLongRealizedOrdersPopup () {
      this.showLongRealizedOrdersPopup = true;
    },
    closeServicesPopup () {
      this.showServicesPopup = false;
    },
    hasDeadlineWarning (order) {
      if (order.warnings.filter(warning => warning.type === 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    hasServicesWarnings (order) {
      if (order.warnings.filter(warning => warning.type !== 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    openPopup (filter, status, dashboardParam) {
      if (filter === 'CONTRACTORS_SETTLEMENTS') {
        this.popupPassDashboardParam = true;
        this.showContractorsSettlementsPopup = true;
        return;
      }

      this.showOrdersPopup = true;

      if (filter !== false) {
        this.popupFilter = filter;
      } else {
        this.popupFilter = '';
      }

      if (status !== false) {
        this.popupStatus = status;
      } else {
        this.popupStatus = '';
      }

      if (dashboardParam) {
        this.popupPassDashboardParam = true;
      } else {
        this.popupPassDashboardParam = false;
      }
    },
    closeOrdersPopup () {
      this.showOrdersPopup = false;
      this.popupFilter = '';
    },
    closeContractorsSettlementsPopup () {
      this.showContractorsSettlementsPopup = false;
    },
    closeContractorsSettlementsImportPopup () {
      this.showContractorsSettlementsImportPopup = false;
    },
    closeCustomersPopup () {
      this.showCustomersPopup = false;
    },
    closeLongRealizedOrdersPopup () {
      this.showLongRealizedOrdersPopup = false;
    },
    getTextColor (value) {
      if (value < 50) {
        return 'text-danger';
      } else if (value >= 50 && value < 100) {
        return 'text-warning';
      }

      return 'text-success';
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-service-popup-open', this.openServicesPopup);
    this.$bus.$off('dashboard-service-popup-close', this.closeServicesPopup);
    this.$bus.$off('dashboard-orders-popup-close', this.closeOrdersPopup);
    this.$bus.$off('dashboard-customers-popup-close', this.closeCustomersPopup);
    this.$bus.$off('dashboard-contractors-settlements-popup-close', this.closeContractorsSettlementsPopup);
    this.$bus.$off('dashboard-contractors-settlements-import-popup-close', this.closeContractorsSettlementsImportPopup);
    this.$bus.$off('dashboard-contractors-settlements-import-popup-open', this.openContractorsSettlementsImportPopup);
    this.$bus.$off('dashboard-long-realized-orders-popup-close', this.closeLongRealizedOrdersPopup);
  }
};
</script>

<style lang="scss" scoped>
.columns-2 {
  flex-basis: auto;
  flex-grow: unset;
  max-width: 50%!important;
  width: 50%;
}

.columns-3 {
  flex-basis: auto;
  flex-grow: unset;
  max-width: calc(100% / 3)!important;
  width: calc(100% / 3);
}

.columns-4 {
  flex-basis: auto;
  flex-grow: unset;
  max-width: 25%!important;
  width: 25%;
}

.columns-2,
.columns-3,
.columns-4 {
  h6 {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 0;
    min-height: 48px;
  }

  h3 {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 80px;
  }

  .btn {
    margin: 0 auto;
    max-width: 100%;
    width: 200px;
  }
}

.card.is-loading {
  position: relative;

  & > * {
    opacity: .33;
  }

  &:before {
    animation: dashboardSpinner 1s linear infinite;
    border: 5px solid #ddd;
    border-top-color: transparent;
    border-radius: 50%;
    content: "";
    height: 48px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translateX(-50%) translateY(-50%) rotate(0deg);
    width: 48px;
  }
}

.alert-icon {
  font-size: 28px;
}

@keyframes dashboardSpinner {
  from {
    transform: translateX(-50%) translateY(-50%) rotate(0deg);
  }

  to {
    transform: translateX(-50%) translateY(-50%) rotate(360deg);
  }
}
</style>
